import { ActionIcon, Text } from '@mantine/core';
import { IconBuildings } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { UserAvatarMenu } from '@/components/shared/UserAvatarMenu';
import BaseClickable from '@/components/ui/BaseClickable';
import { buildOrganizationEventsSearch } from '@/configs/organizationViewSearch';
import { ROUTES } from '@/configs/routes';
import useHistoryNavigationState from '@/hooks/useHistoryNavigationState';
import { useNavigateToOrganizationHome } from '@/hooks/useNavigateToOrganizationHome';
import { organizationsStore, sessionStore } from '@/stores/global/RootStore';
import { useOrganizationSectionsStore } from '@/stores/local/shared/OrganizationSectionsStore';

import NewsHeader from './NewsHeader';
import { buildDetailHeaderConfig } from './buildDetailHeaderConfig';
import type { DetailHeaderState } from './types';

import s from './MobileHeader.module.scss';

const MobileHeader: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const organization = organizationsStore.getOrganizationById(id as string);
  const sectionsStore = useOrganizationSectionsStore();
  const navigateToOrganizationHome = useNavigateToOrganizationHome();
  const historyState = useHistoryNavigationState();

  const { pathname, state } = useLocation();

  const canGoBack = historyState.index > 0;

  const hasEvents = (organization?.events?.length ?? 0) > 0;
  const firstEventId = organization?.events?.[0]?.id;

  const handleBack = React.useCallback(() => {
    if (canGoBack) {
      navigate(ROUTES.root.path);

      return;
    }

    navigate(ROUTES.root.path);
  }, [canGoBack, navigate]);

  const handleHome = React.useCallback(() => {
    const orgId = id ?? '';

    if (hasEvents && sectionsStore.isSectionEnabled('documents')) {
      const base = ROUTES.organization.documents.create(orgId);
      const search = buildOrganizationEventsSearch(firstEventId);

      navigate(`${base}${search}`, { replace: true, state: { resetChat: true } });

      return;
    }

    navigateToOrganizationHome({ organizationId: orgId, isPhone: true, withResetChatState: true });
  }, [firstEventId, hasEvents, id, navigate, navigateToOrganizationHome, sectionsStore]);

  const { avatarLetter, logout } = sessionStore;

  const detailHeader = buildDetailHeaderConfig(pathname, id, state as DetailHeaderState | null);

  if (detailHeader) {
    return <NewsHeader title={detailHeader.title} backTo={detailHeader.backTo} />;
  }

  return (
    <div className={s.root}>
      <ActionIcon className={s.root__back} c="dark" variant="transparent" size="lg" onClick={handleBack}>
        <IconBuildings size={24} />
      </ActionIcon>
      <BaseClickable element="button" onClick={handleHome} className={s.root__title}>
        <Text size="md" fw={600} className={s['root__title-text']}>
          {organization?.name ?? 'Организация'}
        </Text>
      </BaseClickable>

      <UserAvatarMenu
        avatarLetter={avatarLetter}
        onLogout={logout}
        avatarClassName={s.root__avatar}
        dropdownClassName={s.root__dropdown}
      />
    </div>
  );
};

export default observer(MobileHeader);
